import * as Sentry from "@sentry/react";
import { ApiError, apiFetch, hasUserId, setUserCache } from "./client";
import { emitSyncComplete } from "../connectivity";
import { resetFromServer as resetTasks } from "../../features/tasks/store/task-store";
import { resetFromServer as resetEvents } from "../../features/events/store/event-store";
import { resetFromServer as resetHabits } from "../../features/habits/hooks/use-habits";
import { resetFromServer as resetCategories } from "../../shared/stores/category-store";
import { flushQueue, getQueueSize } from "./offline-queue";

const DEFAULT_EMAIL = "local@palnia.app";
// Default password for local development (should be overridden in production via env)
const DEFAULT_PASSWORD = import.meta.env.VITE_LOCAL_PASSWORD || "local123";

async function ensureUser(): Promise<boolean> {
  const existing = localStorage.getItem("palnia_user_id");
  if (existing) return true;

  try {
    const user = await apiFetch<{ id: string; role?: string }>("/users/login", {
      method: "POST",
      body: JSON.stringify({ email: DEFAULT_EMAIL, password: DEFAULT_PASSWORD, name: "Local User" }),
    });
    setUserCache(user.id, user.role);
    return true;
  } catch {
    console.warn("[sync] Failed to create/login user for DB sync");
    return false;
  }
}

/** Returns false if the account no longer exists (401). */
export async function syncFromServer(): Promise<boolean> {
  if (!hasUserId()) return false;

  // Flush any queued offline mutations first
  const queueSize = getQueueSize();
  if (queueSize > 0) {
    const result = await flushQueue(apiFetch);
    emitSyncComplete(result);
    if (result.failed > 0) {
      console.warn(`[sync] ${result.failed} mutations still queued`);
    }
  }

  try {
    const [tasks, events, habits, categories, me] = await Promise.all([
      apiFetch<unknown[]>("/tasks"),
      apiFetch<unknown[]>("/events"),
      apiFetch<unknown[]>("/habits"),
      apiFetch<unknown[]>("/categories"),
      apiFetch<{ role?: string }>("/users/me"),
    ]);
    resetTasks(tasks);
    resetEvents(events);
    resetHabits(habits);
    resetCategories(categories);
    if (me.role) localStorage.setItem("palnia_role", me.role);

    // Clear stale pending IDs — if server returned the data, pending tracking is no longer needed
    localStorage.removeItem("palnia_pending_events");
    localStorage.removeItem("palnia_pending_tasks");
    localStorage.removeItem("palnia_pending_habits");

    return true;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return false;
    console.warn("[sync] Failed to sync from server");
    // Ne pas reporter les erreurs réseau (fetch échoue quand l'utilisateur est hors-ligne)
    const isNetworkError = err instanceof TypeError && err.message === "Failed to fetch";
    if (!isNetworkError) {
      Sentry.captureException(err, { extra: { context: "syncFromServer" } });
    }
    return true;
  }
}

export async function clearAllData(): Promise<void> {
  if (!hasUserId()) return;
  await Promise.all([
    apiFetch("/tasks/import", { method: "POST", body: JSON.stringify({ tasks: [] }) }),
    apiFetch("/events/import", { method: "POST", body: JSON.stringify({ events: [] }) }),
    apiFetch("/habits/import", { method: "POST", body: JSON.stringify({ habits: [] }) }),
  ]);
  resetTasks([]);
  resetEvents([]);
  resetHabits([]);
}

export async function syncImportToDatabase(
  tasks: unknown[],
  events: unknown[],
  habits?: unknown[],
): Promise<void> {
  const ready = await ensureUser();
  if (!ready) return;

  const results = await Promise.allSettled([
    apiFetch("/tasks/import", {
      method: "POST",
      body: JSON.stringify({ tasks }),
    }),
    apiFetch("/events/import", {
      method: "POST",
      body: JSON.stringify({ events }),
    }),
    ...(habits
      ? [
          apiFetch("/habits/import", {
            method: "POST",
            body: JSON.stringify({ habits }),
          }),
        ]
      : []),
  ]);

  for (const result of results) {
    if (result.status === "rejected") {
      console.warn("[sync] Import to DB partially failed:", result.reason);
      Sentry.captureException(result.reason, { extra: { context: "syncImportToDatabase" } });
    }
  }

  // Reload data from server after import to ensure client is in sync
  await syncFromServer();
}

// Online/offline event listener for queue flushing
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    const size = getQueueSize();
    if (size > 0) {
      console.log(`[sync] Retour en ligne — vérification de ${size} mutations en attente`);
      flushQueue(apiFetch).then((result) => {
        if (result.succeeded > 0) {
          console.log(`[sync] ${result.succeeded} mutations synchronisées`);
          syncFromServer().catch(() => {
            // Network error, will retry on next sync
          });
        }
      });
    }
  });
}
